import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ClientSettingsInput } from "@school-sales-ai/shared";
import { api, deleteClientLogoApi, uploadClientLogoApi } from "@/lib/api";
import { useClientSwitcherStore } from "@/stores/client-switcher.store";

/**
 * Agent/widget presentation settings. These used to ride on `PUT /crm/catalog`,
 * which meant changing a colour ran the whole catalog diff and could pop the
 * risky-deactivation modal. They are their own resource now.
 *
 * The read shape carries `logoUrl` and `logoMaxSizeMb`, which the write shape
 * does not: the logo is uploaded through its own multipart endpoint, so it is
 * never part of the JSON settings payload. `logoMaxSizeMb` is the server's own
 * cap, sent so the upload form cannot disagree with what the backend enforces.
 * `visitMode` (read-only) decides whether the meeting-link field is shown.
 */
export type ClientSettings = ClientSettingsInput & {
  logoUrl: string | null;
  logoMaxSizeMb: number;
  /** Where the client's appointments happen, from its script. Read-only. */
  visitMode: "in_person" | "online";
};

export function useSettingsQuery(): ReturnType<
  typeof useQuery<ClientSettings>
> {
  return useQuery<ClientSettings>({
    queryKey: ["settings"],
    queryFn: () => api.get<ClientSettings>("/crm/settings"),
  });
}

export function useSettingsMutation(): ReturnType<
  typeof useMutation<ClientSettings, Error, ClientSettingsInput>
> {
  const queryClient = useQueryClient();

  return useMutation<ClientSettings, Error, ClientSettingsInput>({
    mutationFn: (settings) => api.put<ClientSettings>("/crm/settings", settings),
    onSuccess: () => {
      toast.success("Ajustes guardados.");
      void queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: (error) => {
      toast.error(error.message ?? "Error al guardar los ajustes.");
    },
  });
}

/**
 * The logo commits immediately instead of riding the settings form's dirty/save
 * cycle — a multipart upload cannot be batched into the JSON payload. Both
 * mutations also invalidate the branding query so the sidebar mark updates
 * without a reload.
 */
function useLogoInvalidation(): () => void {
  const queryClient = useQueryClient();
  const activeClientId = useClientSwitcherStore((s) => s.activeClientId);
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["settings"] });
    void queryClient.invalidateQueries({ queryKey: ["crm", "branding", activeClientId] });
  };
}

export function useUploadLogoMutation(): ReturnType<
  typeof useMutation<string, Error, File>
> {
  const invalidate = useLogoInvalidation();
  return useMutation<string, Error, File>({
    mutationFn: (file) => uploadClientLogoApi(file),
    onSuccess: () => {
      toast.success("Imagen institucional actualizada.");
      invalidate();
    },
    onError: (error) => {
      toast.error(error.message ?? "Error al subir la imagen.");
    },
  });
}

export function useDeleteLogoMutation(): ReturnType<
  typeof useMutation<void, Error, void>
> {
  const invalidate = useLogoInvalidation();
  return useMutation<void, Error, void>({
    mutationFn: () => deleteClientLogoApi(),
    onSuccess: () => {
      toast.success("Imagen institucional eliminada.");
      invalidate();
    },
    onError: (error) => {
      toast.error(error.message ?? "Error al eliminar la imagen.");
    },
  });
}
